import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

import { useConfirm } from 'src/contexts/ConfirmContext';
import { setNotificationHasSeen } from 'src/services/state/Notification/actions';
import { getUnseenAndValidNotifications } from 'src/services/state/Notification/getters';

const NotificationsPopup: FC = () => {
  const { t } = useTranslation('translation');
  const confirm = useConfirm();
  const unseenNotifications = getUnseenAndValidNotifications();
  const unseenIds = unseenNotifications.map((n) => n.id).join(',');
  const isOpenRef = useRef(false);

  useEffect(() => {
    if (isOpenRef.current || unseenNotifications.length === 0) return;

    const openNotifications = async () => {
      isOpenRef.current = true;
      try {
        for (const notification of unseenNotifications) {
          let fullUrl = notification.linkUrl || undefined;

          if (
            fullUrl &&
            !fullUrl.startsWith('http://') &&
            !fullUrl.startsWith('https://')
          ) {
            fullUrl = `https://${fullUrl}`;
          }

          const buttonDescription = notification.buttonTitle || t('General.ok');

          await confirm({
            description: notification.text,
            onSecondary: { shouldResolve: false },
            primaryText: buttonDescription,
            secondaryText: t('General.remindLater'),
            title: notification.title,
          })
            .then(() => {
              if (notification.linkUrl) {
                window.open(fullUrl, '_blank', 'noopener,noreferrer');
              }
              setNotificationHasSeen(notification.id);
            })
            .catch((error) => {
              if (error === 'onSecondary') return;
            });
        }
      } finally {
        isOpenRef.current = false;
      }
    };

    openNotifications();
  }, [unseenIds]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default observer(NotificationsPopup);
